import { Injectable } from '@angular/core';
import { asyncScheduler, auditTime, BehaviorSubject, distinctUntilChanged, Observable } from 'rxjs';

export type PageLoadingState = 'initializing' | 'initialized' | 'data-loading' | 'complete' | 'error';

const DEFAULT_COMPONENT_NAME = 'global';

/**
 * @class LoadingService
 * This service manages the loading state of the application.
 * It provides methods to set the loading state and to subscribe to the loading state changes.
 * The loading state is used to display a loading spinner on the screen.
 * While it offers non-component-specific access (not setting the component parameter) for legacy compatibility,
 * it is recommended to use the component parameter to set and retrieve loading states per component.
 * This ensures that components do not reset each other's states.
 *
 * **Attention:** Incorrect usage may leave a component in a dangling loading state and cause endless loading.
 *
 * Note: This class is used by InitializationManager. When you use the InitializationManager, you do not need
 * to set loading states in your components. The InitializationManager will take care of that.
 *
 * @example
 * // in ngOnInit() of a component:
 * this.loadingService.setLoadingState('initializing', this.constructor.name);  // will show loading spinner
 * // do the init work
 * this.loadingService.setLoadingState('data-loading', this.constructor.name); // will still show loading spinner
 * // load all the data
 * this.loadingService.setLoadingState('complete', this.constructor.name);     // will hide loading spinner
 */
@Injectable({ providedIn: 'root' })
export class LoadingService {
    private readonly isLoadingSub = new BehaviorSubject<boolean>(false);
    /**
     * Immediately emits the loading state on change.
     *
     * Intended for TypeScript logic that needs the current state synchronously.
     *
     * Not safe to use in Angular templates when the loading state may change
     * during the same change detection cycle, as this can cause NG0100:
     * `ERROR RuntimeError: NG0100: ExpressionChangedAfterItHasBeenCheckedError: Expression has changed after it was checked.`
     */
    public readonly isLoading$ = this.isLoadingSub.asObservable().pipe(distinctUntilChanged());
    /**
     * Emits a stabilized loading state for Angular template bindings.
     *
     * Emissions are deferred to the next async scheduler tick and rapid synchronous
     * changes are coalesced. This helps avoid NG0100 when the loading state changes
     * during component initialization or change detection.
     *
     * @see isLoading$
     */
    public readonly isLoadingTpl$ = this.isLoading$.pipe(auditTime(0, asyncScheduler));
    // loadingSet tracks all components that are currently in a loading state.
    // As long as at least one component is present, the global isLoading$ will emit true.
    private readonly loadingSet = new Set<string>();
    // componentLoadingStates holds the latest loading state per component.
    // Each component gets its own BehaviorSubject to allow stable subscriptions and access to the last emitted state.
    private readonly componentLoadingStates = new Map<string, BehaviorSubject<PageLoadingState>>();

    public getLoadingState$(component: string = DEFAULT_COMPONENT_NAME): Observable<PageLoadingState> {
        const existingSubject = this.componentLoadingStates.get(component);
        if (existingSubject) {
            return existingSubject.asObservable();
        }
        const subject = this.setComponentLoadingState(component, 'complete');
        return subject.asObservable();
    }

    private setComponentLoadingState(component: string, state: PageLoadingState): BehaviorSubject<PageLoadingState> {
        let subject = this.componentLoadingStates.get(component);
        if (!subject) {
            subject = new BehaviorSubject<PageLoadingState>(state);
        } else {
            subject.next(state);
        }
        this.componentLoadingStates.set(component, subject);
        return subject;
    }

    /**
     * Updates the aggregated loading state of the application.
     *
     * When set to `true` a loading spinner will be displayed that hides the whole content.
     * Accepts an optional but recommended parameter `component`, which allows to set a loading state per component.
     * The loading indicator will in that case only be reset when all components have finished loading.
     * Calling this method emits `isLoading$` when the aggregated loading state changes.
     * @param value Set to `true`, when in loading state and loading indicator shall be displayed.
     *              Set to `false` to reset the loading state when loading is finished.
     * @param component Optional parameter with the component name (has to be unique). When used, it ensures that component
     *                  loading states are properly handled and do not reset each other.
     * @example this.loadingService.setLoading(true, this.constructor.name);
     */
    public setLoading(value: boolean, component = DEFAULT_COMPONENT_NAME): void {
        let emit = true;
        let result = value;
        if (value) {
            if (this.loadingSet.has(component)) {
                console.warn(
                    `LoadingService.setLoading(true) called multiple times for component "${component}"`,
                    new Error('').stack?.toString().substring(5),
                );
                emit = false;
            } else {
                this.loadingSet.add(component);
            }
        } else {
            if (!this.loadingSet.has(component)) {
                console.warn(
                    `LoadingService.setLoading(false) called for component "${component}", which was not set to true.`,
                    this.loadingSet,
                    new Error('').stack?.toString().substring(5),
                );
            }
            this.loadingSet.delete(component);
            if (this.loadingSet.size === 0) {
                result = false;
            } else {
                result = true;
                emit = false;
            }
        }
        if (emit) {
            this.isLoadingSub.next(result);
        }
    }

    public isLoading(): boolean {
        return this.isLoadingSub.getValue();
    }

    /**
     * Sets the loading state of a component.
     *
     * @param state The loading state to set, for example `'initializing'`.
     * @param component The unique component name, recommended to use `this.constructor.name`.
     */
    public setLoadingState(state: PageLoadingState, component = DEFAULT_COMPONENT_NAME): void {
        this.setComponentLoadingState(component, state);
        if (state === 'initializing' || state === 'data-loading') {
            if (!this.loadingSet.has(component)) {
                this.setLoading(true, component);
            }
        } else {
            this.setLoading(false, component);
        }
    }
}
